import {
  Typography as MuiTypography,
  Tooltip as MuiTooltip,
  IconButton as MuiIconButton,
  Grid as MuiGrid,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  ErrorOutline as MuiErrorOutlineIcon,
  Warning as MuiWarningIcon,
  Edit as MuiEditIcon,
  RecordVoiceOver as MuiRecordVoiceOverIcon,
  CloudUpload as MuiCloudUploadIcon,
  Assessment as MuiAssessmentIcon,
  TableChart as MuiTableChartIcon,
  Language as MuiLanguageIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { Trans } from "react-i18next";
import OutreachFailureSummary from "app/shared/UI/OutreachFailureSummary";
import {
  HONEST_BROKER_ASSIGNMENT_STATUS,
  RecruitmentStatus,
  SummaryAccrual,
} from "app/shared/constants";
import Mail from "app/shared/UI/Mail";
import { studyTableStyles } from "./StudyTableStyles";
import ErrorDialog from "app/shared/UI/Dialog/ErrorDialog";
import { handleTableauUrls } from "app/shared/utils";
import { headerStyles, multiLineCell } from "app/shared/UI/sharedStyles";
import { Theme } from "common";

const dialogTitle = (t, riskConditions, irbNumber) =>
  riskConditions.length === 1
    ? t("StudySingleRiskConditionTitle", {
        irbnumber: irbNumber,
      })
    : t("StudyRiskConditionTitle", {
        irbnumber: irbNumber,
        count: riskConditions.length,
      });

const errorSummaryContent = {
  primaryContent: "condition",
  secondaryContent: "recommendation",
};

const ellipsisText = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "110px",
  display: "block",
};

const leadCrcPiWidth = {
  width: "90px",
};
const fixedHeight = {
  height: "20px",
};

export const defaultTableColumnHeaders = (
  t,
  setOpenOutreachFailureSummaryDialog,
  setOutreachFailureSummaryDialogIndex,
  openOutreachFailureSummaryDialog,
  outreachFailureSummaryDialogIndex,
  closeOutreachFailureSummary,
  displayRiskDialog,
  openRiskDialog,
  riskDialogRow,
  closeRiskDialog,
  isLoading,
  setRefresh,
  refresh,
  navigate,
) => [
  {
    field: "irbNumber",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <MuiTooltip
          title={t("studyTable.head_tooltip_irb")}
          placement="top"
          arrow
        >
          <span>{t("studyTable.head_label_irb")}</span>
        </MuiTooltip>
      </MuiTypography>
    ),
    minWidth: 90,
    flex: 1,
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
  },
  {
    field: "protocolNumber",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_protocolNumber" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    minWidth: 100,
    flex: 1,
  },
  {
    field: "earliestConductedDateAfterClear",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_outreach_status" />
      </MuiTypography>
    ),
    renderCell: ({ id, row, api }) => {
      const index = api.getRowIndexRelativeToVisibleRows(id);
      return (
        <MuiTypography sx={multiLineCell}>
          {row.isHavingErrorSummary && (
            <MuiTooltip
              title={t("studyTable.row_tooltip_outreach_status_icon")}
              arrow
            >
              <MuiIconButton
                size="small"
                onClick={(evt) => {
                  setOpenOutreachFailureSummaryDialog(true);
                  setOutreachFailureSummaryDialogIndex(index);
                }}
              >
                <MuiErrorOutlineIcon fontSize="small" color="error" />
              </MuiIconButton>
            </MuiTooltip>
          )}

          {openOutreachFailureSummaryDialog &&
            index === outreachFailureSummaryDialogIndex && (
              <OutreachFailureSummary
                onClose={closeOutreachFailureSummary}
                open={openOutreachFailureSummaryDialog}
                isDisplayConductOutreachButton={true}
                studyId={row.id}
                outreachRefresh={refresh}
                setOutreachRefresh={setRefresh}
                navigate={navigate}
              />
            )}
        </MuiTypography>
      );
    },
    minWidth: 100,
    flex: 1,
  },
  {
    field: "protocolStatus",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_irb_status" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {t("irbStatus." + value)}
      </MuiTypography>
    ),
    minWidth: 100,
    flex: 1,
  },
  {
    field: "protocolSummaryAccrual",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_protocol_summary_accrual" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value
          ? SummaryAccrual.SummaryAccrualTrue
          : SummaryAccrual.SummaryAccrualFalse}
      </MuiTypography>
    ),
    minWidth: 100,
    align: "center",
    flex: 1,
  },
  {
    field: "nickname",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_nickname" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    minWidth: 250,
    flex: 1,
  },
  {
    field: "honestBrokerRecruitmentStatus",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_recruitment" />
        <br />
        <Trans i18nKey="studyTable.head_label_status" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value === RecruitmentStatus.UNINITIATED
          ? ""
          : t(`recruitmentStatus.${value}`)}
      </MuiTypography>
    ),
    minWidth: 150,
    flex: 1,
  },
  {
    field: "leadCrcAndPI",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_lead_crc" />
        <br />
        <Trans i18nKey="studyTable.head_label_pi" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {/* Lead CRC */}
        {row.leadCrc && (
          <MuiTooltip title={row.leadCrcName} placement="top" arrow>
            <div style={leadCrcPiWidth}>
              <Mail emailAddress={row.leadCrc.email}>
                <span
                  style={{
                    ...ellipsisText,
                    ...leadCrcPiWidth,
                    ...fixedHeight,
                    display: "inline-block",
                  }}
                >
                  {row.leadCrcName}
                </span>
              </Mail>
            </div>
          </MuiTooltip>
        )}

        {/* PI */}
        {row.pi && (
          <MuiTooltip title={row.piName} placement="top" arrow>
            <div style={leadCrcPiWidth}>
              <Mail emailAddress={row.pi.email}>
                <span
                  style={{
                    ...ellipsisText,
                    ...leadCrcPiWidth,
                    ...fixedHeight,
                    display: "inline-block",
                  }}
                >
                  {row.piName}
                </span>
              </Mail>
            </div>
          </MuiTooltip>
        )}
      </MuiTypography>
    ),
    minWidth: 100,
    sortable: false,
    flex: 1,
  },
  {
    field: "protocolDeptName,division",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_department" />
        <br />
        <Trans i18nKey="studyTable.head_label_division" />
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {/* Department Name */}
        {row.protocolDeptName ? (
          <MuiTooltip title={row.protocolDeptName} placement="top" arrow>
            <div
              style={{
                ...fixedHeight,
                ...ellipsisText,
              }}
            >
              {row.protocolDeptName}
            </div>
          </MuiTooltip>
        ) : (
          <div
            style={{
              ...fixedHeight,
              ...ellipsisText,
            }}
          />
        )}

        {/* Division */}
        {row.division ? (
          <MuiTooltip title={row.division} placement="top" arrow>
            <div
              style={{
                ...fixedHeight,
                ...ellipsisText,
              }}
            >
              {row.division}
            </div>
          </MuiTooltip>
        ) : (
          <div
            style={{
              ...fixedHeight,
              ...ellipsisText,
            }}
          />
        )}
      </MuiTypography>
    ),
    minWidth: 120,
    flex: 1,
  },
  {
    field: "riskCount",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <MuiTooltip
          title={t("studyTable.head_tooltip_risk")}
          placement="top"
          arrow
        >
          <div>{t("studyTable.head_label_risk")}</div>
        </MuiTooltip>
      </MuiTypography>
    ),
    renderCell: ({ row, api, id }) => {
      const index = api.getRowIndexRelativeToVisibleRows(id);

      return (
        <MuiTypography sx={multiLineCell}>
          {row.honestBrokerRecruitmentStatus ===
          RecruitmentStatus.UNINITIATED ? (
            ""
          ) : row.riskCount > 0 ? (
            <div style={studyTableStyles.riskContainer}>
              <span style={studyTableStyles.riskContainer.riskcount}>
                {row.riskCount}
              </span>
              <span>
                <MuiWarningIcon
                  style={studyTableStyles.riskContainer.warningIcon}
                  fontSize="small"
                  onClick={() => displayRiskDialog(index)}
                />
                {openRiskDialog && index === riskDialogRow && (
                  <ErrorDialog
                    onClose={closeRiskDialog}
                    open={openRiskDialog}
                    errorsList={row.riskConditions}
                    title={dialogTitle(t, row.riskConditions, row.irbNumber)}
                    primarySubHeading={t(`RiskConditionIssue`)}
                    secondarySubHeading={t(`RiskConditionSolution`)}
                    primaryContent={errorSummaryContent.primaryContent}
                    secondaryContent={errorSummaryContent.secondaryContent}
                    isLoading={isLoading}
                  />
                )}
              </span>
            </div>
          ) : (
            <MuiTooltip
              title={t("studyTable.row_tooltip_recruitment_on_track")}
              arrow
            >
              <div>{row.riskCount}</div>
            </MuiTooltip>
          )}
        </MuiTypography>
      );
    },
    minWidth: 80,
    sortable: false,
    flex: 1,
  },
  {
    field: "recruited",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <MuiTooltip
          title={t("studyTable.head_tooltip_recruited")}
          placement="top"
          arrow
        >
          <div>{t("studyTable.head_label_recruited")}</div>
        </MuiTooltip>
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {row.recruited < row.timeElapsed ? (
          <MuiTooltip
            title={t("studyTable.row_tooltip_recruitment_behind")}
            arrow
          >
            <div>{row.recruited}</div>
            <div>
              {"(" +
                row.prospectOnStudy +
                "/" +
                row.protocolTargetAccrual +
                ")"}
            </div>
          </MuiTooltip>
        ) : (
          <MuiTooltip
            title={t("studyTable.row_tooltip_recruitment_on_track")}
            arrow
          >
            <div>{row.recruited}</div>
            <div>
              {"(" +
                row.prospectOnStudy +
                "/" +
                row.protocolTargetAccrual +
                ")"}
            </div>
          </MuiTooltip>
        )}
      </MuiTypography>
    ),
    minWidth: 100,
    sortable: false,
    flex: 1,
  },
  {
    field: "timeElapsed",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <MuiTooltip
          title={t("studyTable.head_tooltip_time_elapsed")}
          placement="top"
          arrow
        >
          <div>{t("studyTable.head_label_time_elapsed")}</div>
        </MuiTooltip>
      </MuiTypography>
    ),
    renderCell: ({ row }) => (
      <MuiTypography sx={multiLineCell}>
        {row.honestBrokerRecruitmentStatus === RecruitmentStatus.UNINITIATED ? (
          ""
        ) : (
          <>
            <div>{row.timeElapsed}</div>
            <div>
              {"(" + row.recruitmentDays + "/" + row.recruitmentTotalDays + ")"}
            </div>
          </>
        )}
      </MuiTypography>
    ),
    minWidth: 100,
    sortable: false,
    flex: 1,
  },
  {
    field: "recruitmentEndDate",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_study_end_date" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value ? format(parseISO(value), "MM/dd/yyyy") : null}
      </MuiTypography>
    ),
    minWidth: 120,
    flex: 1,
  },
  {
    field: "followUpDate",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_follow_up_date" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value ? format(parseISO(value), "MM/dd/yyyy") : null}
      </MuiTypography>
    ),
    minWidth: 100,
    flex: 1,
  },
  {
    field: "note",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_note" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value && (
          <MuiTooltip title={value} arrow>
            <MuiTypography sx={studyTableStyles.noteTableCellHeight}>
              {value}
            </MuiTypography>
          </MuiTooltip>
        )}
      </MuiTypography>
    ),
    minWidth: 120,
    flex: 1,
  },
  {
    field: "actions",
    headerAlign: "left",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="studyTable.head_label_actions" />
      </MuiTypography>
    ),
    type: "actions",
    align: "left",
    minWidth: 100,
    flex: 1,

    renderCell: ({ row }) => {
      return (
        <MuiGrid container justifyContent="space-between">
          <MuiGrid size={6}>
            <MuiTooltip title={t("studyTable.row_tooltip_edit_icon")} arrow>
              <MuiIconButton size="small" sx={{ padding: 0 }}>
                <RouterLink to={`/studies/${row.id}/edit`}>
                  <MuiEditIcon
                    fontSize="small"
                    sx={{ color: Theme.palette.common.black }}
                  />
                </RouterLink>
              </MuiIconButton>
            </MuiTooltip>
          </MuiGrid>
          {row.honestBrokerStatus ===
            HONEST_BROKER_ASSIGNMENT_STATUS.ASSIGNED && (
            <>
              <MuiGrid size={6}>
                <MuiTooltip
                  title={t("studyTable.row_tooltip_ingest_hbe_icon")}
                  arrow
                >
                  <MuiIconButton size="small" sx={{ padding: 0 }}>
                    <RouterLink
                      to={`/prospects/${row.id}/import`}
                      state={{ study: row }}
                    >
                      <MuiCloudUploadIcon
                        fontSize="small"
                        sx={{ color: Theme.palette.common.black }}
                      />
                    </RouterLink>
                  </MuiIconButton>
                </MuiTooltip>
              </MuiGrid>
              <MuiGrid size={6}>
                <MuiTooltip
                  title={t("studyTable.row_tooltip_conduct_outreach_icon")}
                  arrow
                >
                  <MuiIconButton size="small" sx={{ padding: 0 }}>
                    <RouterLink to={`/outreach/${row.id}/conduct`}>
                      <MuiRecordVoiceOverIcon
                        fontSize="small"
                        sx={{ color: Theme.palette.common.black }}
                      />
                    </RouterLink>
                  </MuiIconButton>
                </MuiTooltip>
              </MuiGrid>
              <MuiGrid size={6}>
                <MuiTooltip
                  title={t("studyTable.studySummaryDashboardIconTooltip")}
                  arrow
                >
                  <MuiLink
                    href={`${handleTableauUrls()}/StudySummaryDashboard?irb_number=${
                      row.irbNumber
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MuiIconButton size="small" sx={{ padding: 0 }}>
                      <MuiTableChartIcon
                        fontSize="small"
                        sx={{ color: Theme.palette.common.black }}
                      />
                    </MuiIconButton>
                  </MuiLink>
                </MuiTooltip>
              </MuiGrid>
              <MuiGrid size={6}>
                <MuiTooltip
                  title={t(
                    "studyTable.participantBreakdownDashboardIconTooltip",
                  )}
                  arrow
                >
                  <MuiLink
                    href={`${handleTableauUrls()}/ParticipantBreakdownDashboard?irb_number=${
                      row.irbNumber
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MuiIconButton size="small" sx={{ padding: 0 }}>
                      <MuiAssessmentIcon
                        fontSize="small"
                        sx={{ color: Theme.palette.common.black }}
                      />
                    </MuiIconButton>
                  </MuiLink>
                </MuiTooltip>
              </MuiGrid>
            </>
          )}
          <MuiGrid size={6}>
            <MuiTooltip title="Study Recruitment" arrow>
              <MuiIconButton size="small" sx={{ padding: 0 }}>
                <RouterLink to={`/study-recruitment/${row.protocolNumber}`}>
                  <MuiLanguageIcon
                    fontSize="small"
                    sx={{ color: Theme.palette.common.black }}
                  />
                </RouterLink>
              </MuiIconButton>
            </MuiTooltip>
          </MuiGrid>
        </MuiGrid>
      );
    },
  },
];
